import { useRef, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';

import { PDFViewer, PDFViewerRef } from 'features/pdf-viewer';

import usePurchaseInvoiceData from './usePurchaseInvoiceData';

import NoData from 'components/no-data';
import Loader from 'components/page-loader';
import Error from 'components/error';
import PurchaseInvoiceTitle from './PurchaseInvoiceTitle';
import PurchaseInvoiceMonths from './PurchaseInvoiceMonths';
import LedgerAccountEvents from './LedgerAccountEvents';
import {
  StyledButton,
  StyledCollapse,
  StyledIconButton,
  ToggleButton,
} from './PurchaseInvoice.style';
import PrintIcon from 'shared/icons/Print';
import DownloadIcon from 'shared/icons/Download';
import ArrowBackIcon from 'shared/icons/ArrowBack';
import ArrowForwardIcon from 'shared/icons/ArrowForward';
import KeyboardTabIcon from 'shared/icons/KeyboardTab';

type PurchaseInvoiceProps = {
  invoiceId: string | null;
  canSelectNextInvoice: boolean;
  canSelectPrevInvoice: boolean;
  onSelectNextInvoice: () => void;
  onSelectPrevInvoice: () => void;
};

const PurchaseInvoice = ({
  invoiceId,
  canSelectNextInvoice,
  canSelectPrevInvoice,
  onSelectNextInvoice,
  onSelectPrevInvoice,
}: PurchaseInvoiceProps) => {
  const {
    isError,
    isLoadingLedgerAccountEvents,
    isLoadingPurchaseInvoice,
    isLoadingPurchaseInvoiceMonths,
    isShowedDetails,
    ledgerAccountEvents,
    purchaseInvoiceMonthsQueryState,
    purchaseInvoiceQueryState,
    toggleIsShowedDetails,
  } = usePurchaseInvoiceData(invoiceId);
  const theme = useTheme();
  const matchesMdDown = useMediaQuery(theme.breakpoints.down('md'), {
    noSsr: true,
  });

  const pdfViewerRef = useRef<PDFViewerRef>(null);

  const handlePrintInvoice = () => {
    pdfViewerRef.current?.print();
  };

  const handleDownloadInvoice = () => {
    pdfViewerRef.current?.download(`Invoice_${invoiceId}`);
  };

  const isDisabledInvoiceActions = !!pdfViewerRef.current?.disabled;

  useEffect(() => {
    if (matchesMdDown && !isShowedDetails) {
      toggleIsShowedDetails();
    }
  }, [matchesMdDown]);

  if (isLoadingPurchaseInvoice) return <Loader />;
  if (isError) return <Error />;

  return (
    <Stack spacing={2} flex={1}>
      <PurchaseInvoiceTitle
        sellerInvoiceNumber={
          purchaseInvoiceQueryState.data?.SellerInvoiceNumber ?? null
        }
        sellerOrganizationName={
          purchaseInvoiceQueryState.data?.SellerOrganisationName ?? null
        }
        sellerTotalAmount={purchaseInvoiceQueryState.data?.TotalAmount ?? null}
      />
      <Stack justifyContent="center" spacing={1} direction="row">
        <StyledButton
          disabled={!canSelectPrevInvoice}
          startIcon={<ArrowBackIcon />}
          onClick={onSelectPrevInvoice}
        >
          EDELLINEN
        </StyledButton>
        <StyledButton
          disabled={!canSelectNextInvoice}
          endIcon={<ArrowForwardIcon />}
          onClick={onSelectNextInvoice}
        >
          SEURAAVA
        </StyledButton>
      </Stack>
      <Stack
        justifyContent={matchesMdDown ? 'flex-end' : 'space-between'}
        spacing={1}
        direction="row"
      >
        {!matchesMdDown && (
          <ToggleButton
            onClick={toggleIsShowedDetails}
            endIcon={
              <KeyboardTabIcon
                sx={{
                  transform: `rotate(${isShowedDetails ? 180 : 0}deg)`,
                }}
              />
            }
          >
            {isShowedDetails ? 'Piilota lisätiedot' : 'Näytä lisätiedot'}
          </ToggleButton>
        )}
        <Stack spacing={1} direction="row">
          <StyledIconButton
            disabled={isDisabledInvoiceActions}
            onClick={handlePrintInvoice}
          >
            <PrintIcon />
          </StyledIconButton>
          <StyledIconButton
            disabled={isDisabledInvoiceActions}
            onClick={handleDownloadInvoice}
          >
            <DownloadIcon />
          </StyledIconButton>
        </Stack>
      </Stack>
      <Stack
        flex={1}
        spacing={isShowedDetails ? 4 : 0}
        direction={{
          xs: 'column-reverse',
          md: 'row',
        }}
      >
        <StyledCollapse
          in={isShowedDetails}
          orientation="horizontal"
          unmountOnExit
        >
          <Stack
            direction={{
              xs: 'column-reverse',
              md: 'column',
            }}
            spacing={4}
          >
            <PurchaseInvoiceMonths
              isLoading={isLoadingPurchaseInvoiceMonths}
              monthsInformation={purchaseInvoiceMonthsQueryState.data ?? null}
              sellerOrganizationName={
                purchaseInvoiceQueryState.data?.SellerOrganisationName ?? null
              }
            />
            <LedgerAccountEvents
              isLoading={isLoadingLedgerAccountEvents}
              ledgerAccountEvents={ledgerAccountEvents}
            />
          </Stack>
        </StyledCollapse>

        <Stack flex={2}>
          {purchaseInvoiceQueryState.data?.ImageLink ? (
            <PDFViewer
              ref={pdfViewerRef}
              url={purchaseInvoiceQueryState.data.ImageLink}
            />
          ) : (
            <NoData />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PurchaseInvoice;
